/**
 * CanvasCssFilter — CSS-фильтр на 2D-холсте в ЛЮБОМ движке.
 *
 * `CanvasRenderingContext2D.filter` появился в WebKit только в Safari 18.
 * Значит, на iOS 17 и старше (любой браузер на iPhone — это WebKit) строка
 * `ctx.filter = 'sepia(1)'` не делает НИЧЕГО. Хуже того, её нельзя поймать
 * привычной проверкой `ctx.filter !== 'none'`: у прототипа нет такого свойства,
 * присваивание создаёт обычное JS-свойство объекта, и оно читается обратно тем
 * же текстом. Проверка отвечает «применено», пиксель остаётся прежним — так на
 * этих устройствах молча не работали фильтры фото слайдшоу И не включался
 * запасной путь размытого фона. Замер 27.09.2026 (Playwright WebKit на Windows
 * тоже без `ctx.filter`, то есть честный стенд старого Safari): «Ч/Б» давал
 * цветность кадра 68.7 против 0 в Chromium, край размытой подложки — 84.5
 * против 3.7 (резкий). Обращение того же дня: iPhone iOS 17.5.1, «предпросмотр
 * не с теми параметрами, которые я выбирала».
 *
 * Отсюда три обязательства:
 *   • `native` — честный ответ «движок сам применяет ctx.filter»: свойство
 *     у ПРОТОТИПА И проба пикселем (красный через grayscale(1) стал серым),
 *     а не чтение значения обратно;
 *   • цветовые функции (grayscale, sepia, saturate, hue-rotate, brightness,
 *     contrast, invert, opacity) там, где движок их не умеет, считаются по
 *     пикселям по формулам Filter Effects Module Level 1 (раздел «filter
 *     functions»), `blur` — тремя проходами скользящего среднего (≈ гауссиана);
 *   • ПРОКЛАДКА: где движок фильтр не применяет, `ctx.filter` на обычном и
 *     OffscreenCanvas-контексте начинает работать сам (drawImage, fillRect/
 *     strokeRect, fill/stroke(Path2D), fillText/strokeText) — модуль грузится
 *     на КАЖДОЙ странице из макета, раньше скриптов тулов. Тулу ничего звать
 *     не надо: `ctx.filter = 'blur(8px)'` просто делает то, что написано.
 *     Подробности и границы — у `install()` ниже.
 *
 * Использование (для горячего пути, где кадр рисуется тысячи раз, — прокладка
 * фильтрует каждую операцию, а пара begin/end — весь кадр одним проходом):
 *   var t = CanvasCssFilter.begin(ctx, css);   // native — ставит ctx.filter
 *   ... рисуем ...
 *   CanvasCssFilter.end(ctx, t, x, y, w, h);    // иначе — пересчитывает пиксели области
 *
 * ⚠️ Пиксельный путь фильтрует ИТОГ рисования в области, а родной — каждую
 * операцию отдельно. Для непрозрачного кадра это одно и то же; для
 * полупрозрачных слоёв (затемнение поверх фото) разница — единицы уровней.
 */
(function (root) {
    'use strict';

    // Один экземпляр на страницу: макет грузит модуль всем, и второй
    // экземпляр (старый тег в блейде) не должен ставить прокладку дважды.
    if (root.CanvasCssFilter) return;

    var HOP = Object.prototype.hasOwnProperty;

    // Все браузеры iPhone и Safari на Mac — WebKit, и у всех них vendor такой.
    var APPLE = (function () {
        try { return typeof navigator !== 'undefined' && navigator.vendor === 'Apple Computer, Inc.'; }
        catch (_) { return false; }
    })();

    /* «Умеет ли движок» — по ПИКСЕЛЯМ, а не по свойству. Свойства может не
     * быть вовсе (Safari ≤ 17 без флага — тогда присваивание создаёт обычное
     * JS-свойство и читается обратно), а может и быть, но не работать:
     * QA на настоящем iPhone 14.08.2026 (/photo-frame, doc.js FILTER_NATIVE)
     * видел, как `filter` читается обратно, а drawImage его игнорирует —
     * красный остался rgb(224,31,32) после grayscale. К той же пробе
     * независимо пришли /online-camera (ctxFilterWorks) и /merge-images
     * (harmonize supported). Красный через grayscale(1) обязан стать серым;
     * допуск 16 — на шум, который Safari в приватном режиме подмешивает в
     * чтение холста. Ложное «не умеет» стоит только скорости: прокладка
     * рисует тот же кадр.
     *
     * Пиксели читаются ТОЛЬКО там, где без них не решить: на WebKit (проверить,
     * что свойство не лжёт) и там, где свойства нет вовсе (сможет ли прокладка
     * считать). Chromium и Firefox умеют ctx.filter с 2016 года, а чтение
     * холста при загрузке КАЖДОЙ страницы стоило бы им запроса разрешения в
     * режиме защиты от отпечатков (Tor, resistFingerprinting).
     *
     * readback — читается ли холст честно. Под защитой от отпечатков чтение
     * бывает подменено; тогда прокладка посчитала бы мусор и положила бы его
     * на место картинки, поэтому без честного чтения она не ставится. */
    function greyish(d) {
        return d[3] > 200 && Math.abs(d[0] - d[1]) < 16 && Math.abs(d[1] - d[2]) < 16 && d[0] < 160;
    }
    var PROBE = (function () {
        var r = { dom: false, off: null, readback: null };
        try {
            if (typeof document === 'undefined' || typeof CanvasRenderingContext2D === 'undefined') return r;
            var protoDom = 'filter' in CanvasRenderingContext2D.prototype;
            var hasOff = typeof OffscreenCanvas !== 'undefined' && typeof OffscreenCanvasRenderingContext2D !== 'undefined';
            var protoOff = hasOff && 'filter' in OffscreenCanvasRenderingContext2D.prototype;
            r.dom = protoDom;
            r.off = hasOff ? protoOff : null;
            // (Не-WebKit без фильтра только на OffscreenCanvas — повода читать
            // пиксели на каждой странице нет: там прокладка не ставится.)
            if (!(APPLE || !protoDom)) return r;
            var a = document.createElement('canvas'); a.width = a.height = 1;
            var ax = a.getContext('2d', { willReadFrequently: true });
            ax.fillStyle = '#ff0000'; ax.fillRect(0, 0, 1, 1);
            var d0 = ax.getImageData(0, 0, 1, 1).data;
            r.readback = d0[0] > 239 && d0[1] < 16 && d0[2] < 16 && d0[3] > 239;
            if (!r.readback) return r;
            if (protoDom && APPLE) {
                var b = document.createElement('canvas'); b.width = b.height = 1;
                var bx = b.getContext('2d', { willReadFrequently: true });
                bx.filter = 'grayscale(1)';
                bx.drawImage(a, 0, 0);
                // d[3] в greyish: ничего не нарисовалось — прозрачный чёрный тоже «серый».
                r.dom = greyish(bx.getImageData(0, 0, 1, 1).data);
            }
            if (protoOff && APPLE) {
                var ox = new OffscreenCanvas(1, 1).getContext('2d', { willReadFrequently: true });
                ox.filter = 'grayscale(1)';
                ox.drawImage(a, 0, 0);
                r.off = greyish(ox.getImageData(0, 0, 1, 1).data);
            }
        } catch (_) {}
        return r;
    })();
    var NATIVE = PROBE.dom === true;

    // ── разбор строки фильтра ──────────────────────────────────────────
    // null — строка недопустима или содержит то, что пиксельно не считается
    // (url(), drop-shadow). Недопустимую строку ctx.filter тоже игнорирует.
    var ANGLE = { deg: Math.PI / 180, rad: 1, grad: Math.PI / 200, turn: Math.PI * 2 };

    function num(arg, fn) {
        var s = String(arg).trim();
        if (s === '') return fn === 'hue-rotate' ? 0 : 1;   // значения по умолчанию
        var m = /^(-?\d*\.?\d+(?:e[+-]?\d+)?)(%|deg|rad|grad|turn|px)?$/i.exec(s);
        if (!m) return NaN;
        var v = parseFloat(m[1]), u = (m[2] || '').toLowerCase();
        if (fn === 'hue-rotate') {
            if (u === '') return v === 0 ? 0 : NaN;
            return ANGLE[u] ? v * ANGLE[u] : NaN;
        }
        if (fn === 'blur') {
            if (u === 'px' || (u === '' && v === 0)) return v;
            return NaN;
        }
        if (u === '%') return v / 100;
        if (u !== '') return NaN;
        return v;
    }

    function parse(css) {
        var s = String(css == null ? '' : css).trim();
        if (s === '' || s === 'none') return [];
        var out = [], re = /([a-z-]+)\(([^()]*)\)\s*/gi, m, used = 0;
        while ((m = re.exec(s))) {
            var fn = m[1].toLowerCase();
            if (fn === 'url' || fn === 'drop-shadow') return null;
            if (!/^(grayscale|sepia|saturate|hue-rotate|brightness|contrast|invert|opacity|blur)$/.test(fn)) return null;
            var v = num(m[2], fn);
            if (!isFinite(v) || v < 0 && fn !== 'hue-rotate') return null;
            out.push({ fn: fn, v: v });
            used += m[0].length;
        }
        // хвост, который регулярка не съела, — синтаксическая ошибка
        if (used !== s.length) return null;
        return out;
    }

    // ── цветовые матрицы (строки 3×4 по RGB, значения 0..1) ─────────────
    function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }

    function stepMatrix(fn, v) {
        var s, c, n;
        switch (fn) {
            case 'grayscale':
                s = 1 - clamp01(v);
                return [0.2126 + 0.7874 * s, 0.7152 - 0.7152 * s, 0.0722 - 0.0722 * s, 0,
                        0.2126 - 0.2126 * s, 0.7152 + 0.2848 * s, 0.0722 - 0.0722 * s, 0,
                        0.2126 - 0.2126 * s, 0.7152 - 0.7152 * s, 0.0722 + 0.9278 * s, 0];
            case 'sepia':
                s = 1 - clamp01(v);
                return [0.393 + 0.607 * s, 0.769 - 0.769 * s, 0.189 - 0.189 * s, 0,
                        0.349 - 0.349 * s, 0.686 + 0.314 * s, 0.168 - 0.168 * s, 0,
                        0.272 - 0.272 * s, 0.534 - 0.534 * s, 0.131 + 0.869 * s, 0];
            case 'saturate':
                s = v;
                return [0.213 + 0.787 * s, 0.715 - 0.715 * s, 0.072 - 0.072 * s, 0,
                        0.213 - 0.213 * s, 0.715 + 0.285 * s, 0.072 - 0.072 * s, 0,
                        0.213 - 0.213 * s, 0.715 - 0.715 * s, 0.072 + 0.928 * s, 0];
            case 'hue-rotate':
                c = Math.cos(v); n = Math.sin(v);
                return [0.213 + c * 0.787 - n * 0.213, 0.715 - c * 0.715 - n * 0.715, 0.072 - c * 0.072 + n * 0.928, 0,
                        0.213 - c * 0.213 + n * 0.143, 0.715 + c * 0.285 + n * 0.140, 0.072 - c * 0.072 - n * 0.283, 0,
                        0.213 - c * 0.213 - n * 0.787, 0.715 - c * 0.715 + n * 0.715, 0.072 + c * 0.928 + n * 0.072, 0];
            case 'brightness':
                return [v, 0, 0, 0, 0, v, 0, 0, 0, 0, v, 0];
            case 'contrast':
                n = 0.5 - 0.5 * v;
                return [v, 0, 0, n, 0, v, 0, n, 0, 0, v, n];
            case 'invert':
                s = clamp01(v);
                return [1 - 2 * s, 0, 0, s, 0, 1 - 2 * s, 0, s, 0, 0, 1 - 2 * s, s];
        }
        return null;
    }

    // a∘b — сначала b, потом a (обе 3×4)
    function compose(a, b) {
        var r = new Array(12);
        for (var i = 0; i < 3; i++) {
            for (var j = 0; j < 3; j++) {
                r[i * 4 + j] = a[i * 4] * b[j] + a[i * 4 + 1] * b[4 + j] + a[i * 4 + 2] * b[8 + j];
            }
            r[i * 4 + 3] = a[i * 4] * b[3] + a[i * 4 + 1] * b[7] + a[i * 4 + 2] * b[11] + a[i * 4 + 3];
        }
        return r;
    }

    // Диапазон выхода аффинного отображения m над коробкой входа box
    // [lo0,hi0,lo1,hi1,lo2,hi2] — интервальная арифметика по строкам.
    function range(m, box) {
        var out = new Array(6);
        for (var i = 0; i < 3; i++) {
            var lo = m[i * 4 + 3], hi = lo;
            for (var j = 0; j < 3; j++) {
                var a = m[i * 4 + j], l = box[j * 2], h = box[j * 2 + 1];
                if (a >= 0) { lo += a * l; hi += a * h; } else { lo += a * h; hi += a * l; }
            }
            out[i * 2] = lo; out[i * 2 + 1] = hi;
        }
        return out;
    }

    /**
     * Браузер ограничивает значения 0..1 ПОСЛЕ КАЖДОЙ функции фильтра —
     * замерено против Chromium (27.09.2026): на цепочке «brightness(2)
     * contrast(0.5)» одна свёрнутая матрица ошибается в среднем на 12.3
     * уровня, пошаговая — на 0.6. Но там, где выход шага при ЛЮБОМ входе
     * остаётся в 0..1 (grayscale, яркость ≤ 1…), ограничение ничего не
     * меняет, и шаг честно сворачивается со следующим — это меньше проходов
     * по каждому пикселю при том же результате.
     */
    function fold(groups) {
        var out = [], cur = null, box = [0, 1, 0, 1, 0, 1];
        for (var i = 0; i < groups.length; i++) {
            var m = groups[i];
            if (cur === null) { cur = m; continue; }
            var r = range(cur, box);
            if (r[0] >= -1e-9 && r[1] <= 1 + 1e-9 && r[2] >= -1e-9 && r[3] <= 1 + 1e-9 && r[4] >= -1e-9 && r[5] <= 1 + 1e-9) {
                cur = compose(m, cur);
            } else {
                out.push(cur);
                box = [clamp01(r[0]), clamp01(r[1]), clamp01(r[2]), clamp01(r[3]), clamp01(r[4]), clamp01(r[5])];
                cur = m;
            }
        }
        if (cur) out.push(cur);
        return out;
    }

    /**
     * План пиксельного прохода: цепочка групп. Внутри группы матрицы
     * перемножены (один проход), между группами — ограничение 0..1, как
     * у отдельных примитивов фильтра.
     * { groups: [[12 чисел], ...], alpha: множитель opacity, blur: px }
     */
    function plan(css) {
        var steps = parse(css);
        if (steps === null) return null;
        var groups = [], alpha = 1, blur = 0;
        for (var i = 0; i < steps.length; i++) {
            var st = steps[i];
            if (st.fn === 'opacity') { alpha *= clamp01(st.v); continue; }
            if (st.fn === 'blur') { blur += st.v; continue; }
            var m = stepMatrix(st.fn, st.v);
            if (m) groups.push(m);
        }
        return { groups: fold(groups), alpha: alpha, blur: blur, key: String(css).trim() };
    }

    // ── пиксельный проход ───────────────────────────────────────────────
    function applyToData(d, p) {
        if (!p) return false;
        var g = p.groups, gl = g.length, A = p.alpha;
        if (!gl && A === 1) return false;
        var n = d.length, i, k, r, gg, b, m, R, G, B;
        if (gl === 0) {   // только opacity()
            for (i = 3; i < n; i += 4) d[i] = d[i] * A;
            return true;
        }
        if (gl === 1) {
            // один проход с готовыми коэффициентами — горячий случай
            m = g[0];
            var m0 = m[0], m1 = m[1], m2 = m[2], m3 = m[3] * 255,
                m4 = m[4], m5 = m[5], m6 = m[6], m7 = m[7] * 255,
                m8 = m[8], m9 = m[9], m10 = m[10], m11 = m[11] * 255;
            for (i = 0; i < n; i += 4) {
                r = d[i]; gg = d[i + 1]; b = d[i + 2];
                d[i]     = m0 * r + m1 * gg + m2 * b + m3;
                d[i + 1] = m4 * r + m5 * gg + m6 * b + m7;
                d[i + 2] = m8 * r + m9 * gg + m10 * b + m11;
                if (A !== 1) d[i + 3] = d[i + 3] * A;
            }
            return true;
        }
        // Коэффициенты подряд в одном типизированном массиве: индекс вместо
        // обращения к вложенным массивам на каждом пикселе. Замер в WebKit
        // (кадр 1280×720, «Винтаж», 4 группы после свёртки): 69 → 43 мс.
        var F = new Float64Array(gl * 12), o;
        for (k = 0; k < gl; k++) for (o = 0; o < 12; o++) F[k * 12 + o] = g[k][o];
        var inv = 1 / 255, last = (gl - 1) * 12;
        for (i = 0; i < n; i += 4) {
            r = d[i] * inv; gg = d[i + 1] * inv; b = d[i + 2] * inv;
            for (o = 0; o < last; o += 12) {
                R = F[o] * r + F[o + 1] * gg + F[o + 2] * b + F[o + 3];
                G = F[o + 4] * r + F[o + 5] * gg + F[o + 6] * b + F[o + 7];
                B = F[o + 8] * r + F[o + 9] * gg + F[o + 10] * b + F[o + 11];
                r = R < 0 ? 0 : R > 1 ? 1 : R;
                gg = G < 0 ? 0 : G > 1 ? 1 : G;
                b = B < 0 ? 0 : B > 1 ? 1 : B;
            }
            // последняя группа пишет прямо в Uint8ClampedArray — он сам
            // ограничивает и округляет
            d[i]     = (F[last] * r + F[last + 1] * gg + F[last + 2] * b + F[last + 3]) * 255;
            d[i + 1] = (F[last + 4] * r + F[last + 5] * gg + F[last + 6] * b + F[last + 7]) * 255;
            d[i + 2] = (F[last + 8] * r + F[last + 9] * gg + F[last + 10] * b + F[last + 11]) * 255;
            if (A !== 1) d[i + 3] = d[i + 3] * A;
        }
        return true;
    }

    function applyPixels(ctx, x, y, w, h, css) {
        var p = typeof css === 'string' ? plan(css) : css;
        if (!p || (!p.groups.length && p.alpha === 1)) return false;
        x = Math.max(0, Math.floor(x || 0)); y = Math.max(0, Math.floor(y || 0));
        w = Math.floor(w); h = Math.floor(h);
        if (!(w > 0 && h > 0)) return false;
        var img;
        try { img = ctx.getImageData(x, y, w, h); }
        catch (_) { return false; }   // холст «испорчен» чужим источником — оставить как есть
        applyToData(img.data, p);
        ctx.putImageData(img, x, y);
        return true;
    }

    // ── размытие без ctx.filter ─────────────────────────────────────────
    // Три прохода скользящего среднего ≈ гауссиана с тем же σ (CSS blur(Npx)
    // — это σ = N пикселей холста). Большой радиус считается на уменьшенной
    // копии: сначала честное усреднение пополам (каждый шаг — среднее 2×2,
    // без пропусков пикселей), потом три прохода с остатком σ ≈ 1.6 и
    // растяжение обратно со сглаживанием. Цвет усредняется умноженным на
    // альфу — иначе прозрачные края дают тёмный ореол, как у маски,
    // размытой «в лоб».
    function gaussBoxes(sigma) {
        var n = 3, wl = Math.floor(Math.sqrt(12 * sigma * sigma / n + 1));
        if (wl % 2 === 0) wl--;
        var m = Math.round((12 * sigma * sigma - n * wl * wl - 4 * n * wl - 3 * n) / (-4 * wl - 4));
        var out = [];
        for (var i = 0; i < n; i++) out.push(i < m ? wl : wl + 2);
        return out;
    }
    var BUF_A = null, BUF_B = null;
    function bufs(n) {
        if (!BUF_A || BUF_A.length < n) { BUF_A = new Float32Array(n); BUF_B = new Float32Array(n); }
        return BUF_A;
    }
    // Скользящее среднее радиуса r вдоль строк; за краем — прозрачность.
    function boxH(s, t, w, h, r) {
        var inv = 1 / (2 * r + 1), y, c, x, acc, row;
        for (y = 0; y < h; y++) {
            row = y * w * 4;
            for (c = 0; c < 4; c++) {
                acc = 0;
                for (x = 0; x < r && x < w; x++) acc += s[row + x * 4 + c];
                for (x = 0; x < w; x++) {
                    if (x + r < w) acc += s[row + (x + r) * 4 + c];
                    t[row + x * 4 + c] = acc * inv;
                    if (x - r >= 0) acc -= s[row + (x - r) * 4 + c];
                }
            }
        }
    }
    function boxV(s, t, w, h, r) {
        var inv = 1 / (2 * r + 1), st = w * 4, x, c, y, acc, base;
        for (x = 0; x < w; x++) {
            for (c = 0; c < 4; c++) {
                base = x * 4 + c;
                acc = 0;
                for (y = 0; y < r && y < h; y++) acc += s[base + y * st];
                for (y = 0; y < h; y++) {
                    if (y + r < h) acc += s[base + (y + r) * st];
                    t[base + y * st] = acc * inv;
                    if (y - r >= 0) acc -= s[base + (y - r) * st];
                }
            }
        }
    }
    function blurData(d, w, h, sigma) {
        var n4 = w * h * 4, i, al, a = bufs(n4), b = BUF_B;
        for (i = 0; i < n4; i += 4) {
            al = d[i + 3] / 255;
            a[i] = d[i] * al; a[i + 1] = d[i + 1] * al; a[i + 2] = d[i + 2] * al; a[i + 3] = d[i + 3];
        }
        var sz = gaussBoxes(sigma);
        for (var k = 0; k < 3; k++) {
            var r = (sz[k] - 1) >> 1;
            if (r < 1) continue;
            boxH(a, b, w, h, r);
            boxV(b, a, w, h, r);
        }
        for (i = 0; i < n4; i += 4) {
            var A = a[i + 3];
            if (A < 0.5) { d[i] = d[i + 1] = d[i + 2] = d[i + 3] = 0; continue; }
            var q = 255 / A;
            d[i] = a[i] * q; d[i + 1] = a[i + 1] * q; d[i + 2] = a[i + 2] * q; d[i + 3] = A;
        }
    }

    // Служебные холсты — переиспользуются: в цикле кадров видео размер один и
    // тот же, и новый холст на каждый кадр стоил бы памяти, а не только времени.
    function mk(w, h, read) {
        var c = document.createElement('canvas');
        c.width = w; c.height = h;
        return { c: c, x: c.getContext('2d', read ? { willReadFrequently: true } : undefined) };
    }
    // Холст больше нужного переиспользуется (рисуется в угол 0,0 — все
    // проходы берут прямоугольник w×h явно): текст рисуется буква за буквой, и
    // новый размер на каждую букву стоил бы перевыделения холста. Больше
    // вчетверо — пересоздаётся, чтобы не держать память крупного кадра.
    var POOL = {};
    function pooled(key, w, h, read) {
        var s = POOL[key];
        if (!s) { s = POOL[key] = mk(w, h, read); return s; }
        var cw = s.c.width, ch = s.c.height;
        if (cw < w || ch < h || cw * ch > 4 * w * h + 65536) { s.c.width = w; s.c.height = h; return s; }
        s.x.setTransform(1, 0, 0, 1, 0, 0);
        s.x.globalCompositeOperation = 'source-over';
        // с запасом в 2 пикселя: сглаживание на краю прямоугольника читает соседей
        s.x.clearRect(0, 0, Math.min(cw, w + 2), Math.min(ch, h + 2));
        return s;
    }
    // Пикселей, которые три прохода считают без уменьшения. Рабочие буферы —
    // два Float32 по 4 канала: 0.3 Мп = 9.6 МБ, и они живут, пока жива
    // страница (в цикле кадров их пересоздание стоило бы сборок мусора).
    // Большая область с малым σ уменьшается чуть сильнее, чем нужно, —
    // размытие выходит на доли пикселя шире.
    var FULLRES_MAX = 3e5;

    // Размыть холст c (весь) на σ пикселей. false — пиксели не прочитать
    // (холст «испорчен» чужим источником): тогда осталось только
    // уменьшить-увеличить, без трёх проходов.
    function blurCanvas(c, x, w, h, sigma, draw) {
        if (!(sigma >= 0.35)) return true;
        var f = sigma > 1.6 ? sigma / 1.6 : 1;
        if (w * h / (f * f) > FULLRES_MAX) f = Math.sqrt(w * h / FULLRES_MAX);
        var ok = true, img;
        if (f <= 1) {
            try { img = x.getImageData(0, 0, w, h); } catch (_) { return false; }
            blurData(img.data, w, h, sigma);
            x.putImageData(img, 0, 0);
            return true;
        }
        var lw = Math.max(1, Math.round(w / f)), lh = Math.max(1, Math.round(h / f));
        var src = c, sw = w, sh = h, flip = 0, t;
        while (sw >= lw * 2 && sh >= lh * 2) {
            var nw = Math.ceil(sw / 2), nh = Math.ceil(sh / 2);
            t = pooled(flip ? 'h1' : 'h0', nw, nh, false);
            t.x.imageSmoothingEnabled = true;
            draw.call(t.x, src, 0, 0, sw, sh, 0, 0, nw, nh);
            src = t.c; sw = nw; sh = nh; flip ^= 1;
        }
        var L = pooled('low', lw, lh, true);
        L.x.imageSmoothingEnabled = true;
        draw.call(L.x, src, 0, 0, sw, sh, 0, 0, lw, lh);
        // Усреднение пополам и растяжение сами немного размывают (≈ f²/3 по
        // дисперсии) — остаток добирают три прохода.
        var sl = sigma * Math.sqrt(lw * lh / (w * h));
        sl = Math.sqrt(Math.max(0.25, sl * sl - 1 / 3));
        try { img = L.x.getImageData(0, 0, lw, lh); blurData(img.data, lw, lh, sl); L.x.putImageData(img, 0, 0); }
        catch (_) { ok = false; }
        x.setTransform(1, 0, 0, 1, 0, 0);
        x.globalCompositeOperation = 'copy';
        x.imageSmoothingEnabled = true;
        try { x.imageSmoothingQuality = 'high'; } catch (_) {}
        draw.call(x, L.c, 0, 0, lw, lh, 0, 0, w, h);
        x.globalCompositeOperation = 'source-over';
        return ok;
    }

    // ── прокладка: ctx.filter, который РАБОТАЕТ там, где движок его не умеет ──
    // Ставится только туда, где проба не увидела фильтра (Safari ≤ 17 — любой
    // браузер на iPhone с iOS 17 и старше; сборки WebKit со «свойством-эхом»).
    // Chromium, Firefox и Safari 18+ её не видят вовсе. Замер 27.09.2026:
    // 61 место на сайте пишет ctx.filter, у 31 из них ГОТОВЫЙ файл без эффекта
    // на таких устройствах, в том числе четыре приватных: размытие, которым
    // человек прятал текст или лицо, в выданном видео не применялось. Чинить
    // каждое место по отдельности — 43 файла, и следующий тул напишет
    // `ctx.filter = …` снова; поэтому исправлен сам ctx.filter.
    //
    // Как рисует: фигура или картинка рисуется на служебный холст в
    // координатах ПИКСЕЛЕЙ целевого холста (та же матрица, сдвинутая на
    // угол области), там фильтруется — цвет теми же матрицами, что выше,
    // размытие — blurCanvas — и кладётся на место одним drawImage без
    // матрицы. Прозрачность, режим наложения, тень и обрезка целевого
    // контекста применяются к ОТФИЛЬТРОВАННОМУ — порядок, как в спецификации.
    // Размытие задано в пикселях холста и от матрицы не зависит — как у движков.
    //
    // Что не воспроизводится: fill()/stroke() по ТЕКУЩЕМУ пути (путь не
    // переснять — Path2D переснимается), drop-shadow и url(). Такое рисуется
    // без фильтра, как раньше, и один раз сообщается (canvas_filter_shim).
    var SHIM = { dom: false, offscreen: false };
    var NOTED = {};
    // ms — сколько стоила ПЕРВАЯ такая отрисовка на этом устройстве: цену
    // прокладки на настоящих iPhone знает только поле, стенд её не покажет.
    function note(kind, what, ms, px) {
        var k = kind + '|' + what;
        if (NOTED[k]) return;
        NOTED[k] = 1;
        try {
            if (typeof root.__ae === 'function') {
                var e = { f: 'canvas_filter_shim', kind: kind, what: String(what).slice(0, 80) };
                if (ms != null) { e.ms = Math.round(ms); e.mpx = Math.round(px / 1e4) / 100; }
                root.__ae('feature_use', e);
            }
        } catch (_) {}
    }
    function fnNames(css) {
        var out = [], re = /([a-z-]+)\(/gi, m;
        while ((m = re.exec(css))) out.push(m[1].toLowerCase());
        return out.join(',');
    }

    function stateOf(ctx) {
        var s = ctx.__cfs;
        if (!s) {
            s = { f: 'none', p: null, stack: [] };
            try { Object.defineProperty(ctx, '__cfs', { value: s, writable: true, configurable: true }); }
            catch (_) { ctx.__cfs = s; }
            // Холсту — та же ссылка: смена его размера сбрасывает состояние
            // контекста, а достать контекст из холста без getContext нельзя.
            try {
                var cv = ctx.canvas;
                if (cv) Object.defineProperty(cv, '__cfs', { value: s, writable: true, configurable: true });
            } catch (_) {}
        }
        return s;
    }
    function resetState(s) { s.f = 'none'; s.p = null; s.stack.length = 0; }

    function getFilter() { var s = this.__cfs; return s ? s.f : 'none'; }
    function setFilter(v) {
        var str = String(v), t = str.trim();
        if (!t) return;                       // как у движка: пустая строка не значение
        var s = stateOf(this);
        if (t.toLowerCase() === 'none') { s.f = 'none'; s.p = null; return; }
        var p = plan(t);
        if (p === null) {
            // Правильный CSS, который прокладка не считает: движок принял бы
            // строку — принимаем, но рисуем без фильтра и говорим об этом.
            // Синтаксическую ошибку движок молча отбрасывает — и мы тоже.
            if (/\b(?:drop-shadow|url)\s*\(/i.test(t)) { s.f = str; s.p = null; note('unsupported', fnNames(t)); }
            return;
        }
        s.f = str;
        s.p = (p.groups.length || p.alpha !== 1 || p.blur > 0) ? p : null;
    }
    // Присвоено ДО установки прокладки (inline-скрипт во время разбора
    // страницы): на Safari ≤ 17 это обычное свойство объекта, и оно
    // заслонило бы аксессор прототипа навсегда.
    function adopt(ctx) {
        var v;
        try { v = ctx.filter; delete ctx.filter; } catch (_) { return; }
        stateOf(ctx);
        if (v != null) { try { ctx.filter = v; } catch (_) {} }
    }

    // Область на целевом холсте, которую затронет рисование: углы фигуры через
    // матрицу + запас на размытие (3σ), в пределах холста с тем же запасом.
    function region(ctx, m, pts, grow, blur) {
        var cw = ctx.canvas.width, ch = ctx.canvas.height, pad = blur > 0 ? Math.ceil(blur * 3) : 0;
        var x0, y0, x1, y1;
        if (pts) {
            x0 = y0 = Infinity; x1 = y1 = -Infinity;
            for (var i = 0; i < pts.length; i += 2) {
                var X = m.a * pts[i] + m.c * pts[i + 1] + m.e, Y = m.b * pts[i] + m.d * pts[i + 1] + m.f;
                if (X < x0) x0 = X; if (X > x1) x1 = X;
                if (Y < y0) y0 = Y; if (Y > y1) y1 = Y;
            }
            if (!(isFinite(x0) && isFinite(y0) && isFinite(x1) && isFinite(y1))) return null;
            if (grow) {
                var g = grow * Math.sqrt(Math.abs(m.a * m.d - m.b * m.c)) + 1;
                x0 -= g; y0 -= g; x1 += g; y1 += g;
            }
        } else {
            x0 = 0; y0 = 0; x1 = cw; y1 = ch;
        }
        x0 = Math.max(Math.floor(x0) - pad, -pad); y0 = Math.max(Math.floor(y0) - pad, -pad);
        x1 = Math.min(Math.ceil(x1) + pad, cw + pad); y1 = Math.min(Math.ceil(y1) + pad, ch + pad);
        if (x1 <= x0 || y1 <= y0 || x1 <= 0 || y1 <= 0 || x0 >= cw || y0 >= ch) return null;
        return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
    }

    var STYLE = ['fillStyle', 'strokeStyle', 'lineWidth', 'lineCap', 'lineJoin', 'miterLimit', 'lineDashOffset',
                 'imageSmoothingEnabled', 'imageSmoothingQuality'];
    var TEXT = ['font', 'textAlign', 'textBaseline', 'letterSpacing', 'wordSpacing', 'fontKerning',
                'fontStretch', 'fontVariantCaps', 'textRendering'];
    function copyState(from, to, text) {
        var i, k;
        for (i = 0; i < STYLE.length; i++) { k = STYLE[i]; if (k in from) { try { to[k] = from[k]; } catch (_) {} } }
        try { to.setLineDash(from.getLineDash()); } catch (_) {}
        if (!text) return;
        for (i = 0; i < TEXT.length; i++) { k = TEXT[i]; if (k in from) { try { to[k] = from[k]; } catch (_) {} } }
        // 'inherit' у служебного холста значило бы «слева направо» —
        // берём то направление, которое реально у целевого.
        try {
            var dir = from.direction;
            if (dir === 'inherit' && from.canvas && from.canvas.nodeType === 1 && from.canvas.isConnected) {
                dir = getComputedStyle(from.canvas).direction || 'ltr';
            }
            to.direction = dir === 'inherit' ? 'ltr' : dir;
        } catch (_) {}
    }

    // Нарисовать операцию op с аргументами args через фильтр состояния s.
    // O — исходные методы прототипа ЦЕЛЕВОГО контекста, D — служебного холста.
    function filtered(ctx, s, O, D, op, args) {
        var t0 = typeof performance !== 'undefined' ? performance.now() : 0;
        var p = s.p, m = ctx.getTransform(), pts = null, grow = 0;
        if (op === 'drawImage') {
            var n = args.length, dx, dy, dw, dh;
            if (n >= 9) { dx = args[5]; dy = args[6]; dw = args[7]; dh = args[8]; }
            else if (n >= 5) { dx = args[1]; dy = args[2]; dw = args[3]; dh = args[4]; }
            else { var z = srcSize(args[0]); dx = args[1]; dy = args[2]; dw = z.w; dh = z.h; }
            pts = [dx, dy, dx + dw, dy, dx, dy + dh, dx + dw, dy + dh];
        } else if (op === 'fillRect' || op === 'strokeRect') {
            var rx = +args[0], ry = +args[1], rw = +args[2], rh = +args[3];
            pts = [rx, ry, rx + rw, ry, rx, ry + rh, rx + rw, ry + rh];
            if (op === 'strokeRect') grow = ctx.lineWidth || 1;
        } else if (op === 'fillText' || op === 'strokeText') {
            var tm = null;
            try { tm = ctx.measureText(args[0]); } catch (_) {}
            if (tm && isFinite(tm.actualBoundingBoxLeft) && isFinite(tm.actualBoundingBoxAscent)) {
                var tx = +args[1], ty = +args[2];
                pts = [tx - tm.actualBoundingBoxLeft, ty - tm.actualBoundingBoxAscent,
                       tx + tm.actualBoundingBoxRight, ty - tm.actualBoundingBoxAscent,
                       tx - tm.actualBoundingBoxLeft, ty + tm.actualBoundingBoxDescent,
                       tx + tm.actualBoundingBoxRight, ty + tm.actualBoundingBoxDescent];
                grow = (op === 'strokeText' ? ctx.lineWidth || 1 : 0) + 2;
            }
        }
        // fill/stroke(Path2D) и текст без метрик: границ не знаем — весь холст.
        var reg = region(ctx, m, pts, grow, p.blur);
        if (!reg) return;
        // Служебный холст не больше 8 Мп — у iOS до 18 холст больше 16.7 Мп
        // молча остаётся пустым, а память телефона не резиновая.
        var k = Math.min(1, Math.sqrt(MAX_AREA / (reg.w * reg.h)));
        var sw = Math.max(1, Math.ceil(reg.w * k)), sh = Math.max(1, Math.ceil(reg.h * k));
        var S = pooled('scr', sw, sh, false), x = S.x;
        x.setTransform(m.a * k, m.b * k, m.c * k, m.d * k, (m.e - reg.x) * k, (m.f - reg.y) * k);
        x.globalAlpha = 1;
        x.globalCompositeOperation = 'source-over';
        x.shadowColor = 'rgba(0,0,0,0)';
        copyState(ctx, x, op === 'fillText' || op === 'strokeText');
        try { D[op].apply(x, args); }
        catch (e) { throw { cfNative: e }; }   // ошибка самого рисования — отдаём как есть
        var ok = true;
        if (p.blur > 0) ok = blurCanvas(S.c, x, sw, sh, p.blur * k, D.drawImage);
        if (p.groups.length || p.alpha !== 1) {
            try {
                var img = x.getImageData(0, 0, sw, sh);
                applyToData(img.data, p);
                x.putImageData(img, 0, 0);
            } catch (_) { ok = false; }
        }
        if (!ok) note('unreadable', op);
        O.save.call(ctx);
        try {
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            if (k < 1) ctx.imageSmoothingEnabled = true;
            O.drawImage.call(ctx, S.c, 0, 0, sw, sh, reg.x, reg.y, reg.w, reg.h);
        } finally {
            O.restore.call(ctx);
        }
        note('draw', op + ':' + fnNames(p.key), t0 ? performance.now() - t0 : null, sw * sh);
    }

    var OPS = ['drawImage', 'fillRect', 'strokeRect', 'fill', 'stroke', 'fillText', 'strokeText'];
    function originals(P) {
        var O = {};
        OPS.concat(['save', 'restore', 'reset']).forEach(function (k) {
            if (typeof P[k] === 'function') O[k] = P[k];
        });
        return O;
    }
    var D_ORIG = null;   // исходные методы DOM-контекста: ими рисует служебный холст

    function install(P, name) {
        if (!P || HOP.call(P, '__cfShim') || typeof P.getTransform !== 'function') return false;
        var O = originals(P);
        if (!O.drawImage || !O.save || !O.restore) return false;
        try {
            Object.defineProperty(P, 'filter', { configurable: true, enumerable: true, get: getFilter, set: setFilter });
        } catch (_) { return false; }
        OPS.forEach(function (op) {
            if (!O[op]) return;
            P[op] = function () {
                var s = this.__cfs;
                if (s === undefined && HOP.call(this, 'filter')) { adopt(this); s = this.__cfs; }
                if (!s || !s.p) return O[op].apply(this, arguments);
                if ((op === 'fill' || op === 'stroke') && !(typeof Path2D !== 'undefined' && arguments[0] instanceof Path2D)) {
                    note('skip', op + ':' + fnNames(s.p.key));
                    return O[op].apply(this, arguments);
                }
                try {
                    return filtered(this, s, O, D_ORIG, op, arguments);
                } catch (e) {
                    if (e && e.cfNative) throw e.cfNative;
                    note('error', op + ':' + String((e && e.message) || e).slice(0, 50));
                    return O[op].apply(this, arguments);
                }
            };
        });
        P.save = function () {
            var s = stateOf(this);
            s.stack.push(s.f, s.p);
            return O.save.apply(this, arguments);
        };
        P.restore = function () {
            var s = this.__cfs;
            if (s && s.stack.length) { s.p = s.stack.pop(); s.f = s.stack.pop(); }
            return O.restore.apply(this, arguments);
        };
        if (O.reset) {
            P.reset = function () {
                var s = this.__cfs;
                if (s) resetState(s);
                return O.reset.apply(this, arguments);
            };
        }
        try { Object.defineProperty(P, '__cfShim', { value: name }); } catch (_) {}
        return true;
    }
    // Смена размера холста сбрасывает всё состояние контекста — и фильтр тоже.
    function hookSize(P) {
        if (!P) return;
        ['width', 'height'].forEach(function (k) {
            var d = Object.getOwnPropertyDescriptor(P, k);
            if (!d || !d.set || !d.configurable) return;
            try {
                Object.defineProperty(P, k, {
                    configurable: true, enumerable: d.enumerable, get: d.get,
                    set: function (v) { d.set.call(this, v); var s = this.__cfs; if (s) resetState(s); }
                });
            } catch (_) {}
        });
    }
    if (PROBE.readback && typeof document !== 'undefined') {
        D_ORIG = originals(CanvasRenderingContext2D.prototype);
        if (!NATIVE && install(CanvasRenderingContext2D.prototype, 'dom')) {
            SHIM.dom = true;
            hookSize(typeof HTMLCanvasElement !== 'undefined' ? HTMLCanvasElement.prototype : null);
        }
        if (PROBE.off === false && install(OffscreenCanvasRenderingContext2D.prototype, 'offscreen')) {
            SHIM.offscreen = true;
            hookSize(typeof OffscreenCanvas !== 'undefined' ? OffscreenCanvas.prototype : null);
        }
    }
    // WebKit, где фильтр не работает, а прокладка не встала (холст не читается
    // честно): эффекты там снова пропадут молча — пусть поле об этом знает.
    // Через 4 с: телеметрия страницы подключается позже этого модуля.
    if (APPLE && !NATIVE && !SHIM.dom && typeof setTimeout === 'function') {
        setTimeout(function () { note('not_installed', 'readback:' + PROBE.readback); }, 4000);
    }

    function isOffscreen(ctx) {
        return typeof OffscreenCanvasRenderingContext2D !== 'undefined' && ctx instanceof OffscreenCanvasRenderingContext2D;
    }

    // ── размытие, которое ПРЯЧЕТ ────────────────────────────────────────
    // Замер 27.09.2026 (tests/playwright/redaction-blur-hides-on-every-engine.mjs,
    // полосы 1 px — самая читаемая деталь): цензура /remove-text-from-video и
    // размытие области /video-editor размывали ТОЛЬКО пиксели самой области.
    // За её краем размывать нечего — прозрачность, и размытое пятно к краю
    // становится прозрачным: в полосе 3 px вдоль края оставалось 120 из 255
    // уровней исходных полос — ВО ВСЕХ браузерах, Chrome тоже. Ровно там стоят
    // первая и последняя буквы номера. /redact-photo брал поле 2σ (у гауссианы
    // хвост до 3σ), /screenshot — 1σ, /video-blur — весь кадр, но край КАДРА
    // так же выцветал поверх исходника.
    //
    // Отсюда одно правило на все тулы: размывается копия области С ПОЛЕМ ≥ 3σ
    // настоящих соседей, а за краем холста поле добирается повтором крайних
    // пикселей (так делают фоторедакторы) — размытое непрозрачно до самого
    // края, и под ним нечему просвечивать.
    //
    // paddedCopy — копия [x, x+w)×[y, y+h) с полем pad со всех сторон, на
    // служебном холсте (в углу 0,0; w+2pad × h+2pad).
    // Исходный drawImage, а не обёртка прокладки: служебный холст фильтра не несёт.
    var DRAW = (D_ORIG && D_ORIG.drawImage) || (typeof CanvasRenderingContext2D !== 'undefined' ? CanvasRenderingContext2D.prototype.drawImage : null);
    function paddedCopy(src, x, y, w, h, pad) {
        if (!DRAW || typeof document === 'undefined') return null;
        var sz = srcSize(src), W = sz.w, H = sz.h;
        x = Math.floor(x); y = Math.floor(y); w = Math.ceil(w); h = Math.ceil(h); pad = Math.max(0, Math.ceil(pad));
        var pw = w + 2 * pad, ph = h + 2 * pad;
        var sx0 = Math.max(0, x - pad), sy0 = Math.max(0, y - pad);
        var sx1 = Math.min(W, x + w + pad), sy1 = Math.min(H, y + h + pad);
        var aw = sx1 - sx0, ah = sy1 - sy0;
        if (!(aw > 0 && ah > 0 && pw > 0 && ph > 0)) return null;
        var P = pooled('pad', pw, ph, false), X = P.x;
        X.imageSmoothingEnabled = false;   // повтор края — ровно тот же пиксель
        var ox = sx0 - (x - pad), oy = sy0 - (y - pad);
        DRAW.call(X, src, sx0, sy0, aw, ah, ox, oy, aw, ah);
        if (ox > 0) DRAW.call(X, P.c, ox, oy, 1, ah, 0, oy, ox, ah);
        var rx = ox + aw;
        if (rx < pw) DRAW.call(X, P.c, rx - 1, oy, 1, ah, rx, oy, pw - rx, ah);
        if (oy > 0) DRAW.call(X, P.c, 0, oy, pw, 1, 0, 0, pw, oy);
        var by = oy + ah;
        if (by < ph) DRAW.call(X, P.c, 0, by - 1, pw, 1, 0, by, pw, ph - by);
        return { canvas: P.c, w: pw, h: ph, pad: pad, x: x - pad, y: y - pad };
    }

    /**
     * Спрятать область размытием — так, что из неё не уцелеет ничего.
     *   ctx      — куда рисовать (координаты — пиксели холста, матрица игнорируется)
     *   src      — откуда брать пиксели (null — сам ctx.canvas, «размыть на месте»)
     *   x,y,w,h  — область; sigma — сила размытия, px
     *   opts.clip(ctx) — своя форма (эллипс, скруглённый прямоугольник): строит
     *              путь в тех же пикселях; без неё — прямоугольник
     * Область ЗАМЕНЯЕТСЯ (globalCompositeOperation 'copy' внутри формы): у PNG с
     * прозрачностью размытое пятно полупрозрачно, и поверх исходника его
     * контуры читались бы сквозь него.
     * Где ctx.filter не даёт эффекта вовсе (прокладку не поставить — холст не
     * читается), размытие делается уменьшением-увеличением: прятать надо
     * всегда, красота — потом.
     * → 'native' | 'shim' | 'scaled' | '' (нечего прятать)
     */
    function blurHide(ctx, src, x, y, w, h, sigma, opts) {
        opts = opts || {};
        src = src || ctx.canvas;
        sigma = Math.max(1, +sigma || 0);
        var pad = Math.ceil(sigma * 3) + 2;
        var P = paddedCopy(src, x, y, w, h, pad);
        if (!P) return '';
        var how = worksOn(ctx) ? (isOffscreen(ctx) ? (PROBE.off === true ? 'native' : 'shim') : (NATIVE ? 'native' : 'shim')) : 'scaled';
        ctx.save();
        try {
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.beginPath();
            if (typeof opts.clip === 'function') opts.clip(ctx);
            else ctx.rect(Math.floor(x), Math.floor(y), Math.ceil(w), Math.ceil(h));
            ctx.clip();
            ctx.globalCompositeOperation = 'copy';
            ctx.globalAlpha = 1;
            if (how !== 'scaled') {
                ctx.filter = 'blur(' + sigma + 'px)';
                ctx.drawImage(P.canvas, 0, 0, P.w, P.h, P.x, P.y, P.w, P.h);
            } else {
                // Уменьшить честным усреднением пополам до клеток ~σ и растянуть.
                var f = Math.max(2, sigma), cw = P.w, ch = P.h, s = P.canvas, flip = 0, t;
                var lw = Math.max(1, Math.round(P.w / f)), lh = Math.max(1, Math.round(P.h / f));
                while (cw >= lw * 2 && ch >= lh * 2) {
                    var nw = Math.ceil(cw / 2), nh = Math.ceil(ch / 2);
                    t = pooled(flip ? 'hs1' : 'hs0', nw, nh, false);
                    t.x.imageSmoothingEnabled = true;
                    DRAW.call(t.x, s, 0, 0, cw, ch, 0, 0, nw, nh);
                    s = t.c; cw = nw; ch = nh; flip ^= 1;
                }
                ctx.imageSmoothingEnabled = true;
                try { ctx.imageSmoothingQuality = 'high'; } catch (_) {}
                ctx.drawImage(s, 0, 0, cw, ch, P.x, P.y, P.w, P.h);
            }
        } finally {
            ctx.restore();
        }
        return how;
    }
    // ctx.filter на этом контексте даст эффект — сам движок или прокладка.
    function worksOn(ctx) {
        if (isOffscreen(ctx)) return PROBE.off === true || SHIM.offscreen;
        return NATIVE || SHIM.dom;
    }

    /**
     * begin/end — пара вокруг рисования.
     *   native  → ctx.filter = css, end() снимает;
     *   pixels  → ctx не трогаем, end() пересчитывает область;
     *   none    → фильтра нет или его не посчитать (url(), drop-shadow).
     * Размытие в пиксельном режиме НЕ воспроизводится — кому оно нужно,
     * спрашивает `blurOf()` и рисует через `drawBlurred`.
     */
    function begin(ctx, css) {
        var s = String(css == null ? '' : css).trim();
        if (!s || s === 'none') return { mode: 'none' };
        if (NATIVE) {
            try { ctx.filter = s; return { mode: 'native' }; }
            catch (_) { return { mode: 'none' }; }
        }
        var p = plan(s);
        if (!p || (!p.groups.length && p.alpha === 1)) return { mode: 'none' };
        return { mode: 'pixels', plan: p };
    }

    function end(ctx, token, x, y, w, h) {
        if (!token) return false;
        if (token.mode === 'native') { try { ctx.filter = 'none'; } catch (_) {} return true; }
        if (token.mode === 'pixels') return applyPixels(ctx, x, y, w, h, token.plan);
        return false;
    }

    /**
     * Тонированная копия НЕПОДВИЖНОГО источника — для горячего пути, где кадр
     * рисуется десятки раз в секунду. Проход по пикселям кадра 1280×720 стоит
     * 42–69 мс в WebKit на десктопе (замер 27.09.2026; на телефоне больше), то
     * есть минуты сверху к экспорту в тысячи кадров. Цветовой фильтр от места
     * пикселя не зависит, поэтому один проход по ФОТО до рисования даёт тот же
     * кадр, что проход по кадру после, — и стоит один раз на фото.
     *   needW/needH — сколько пикселей источника реально нужно (0 — сколько есть);
     *   копия никогда не больше MAX_AREA: у iOS до 18 холст больше 16.7 Мп
     *   молча остаётся пустым.
     * Кэш — несколько последних копий: рисующему нужны текущий и следующий
     * слайд, а держать тонированную копию всей ленты на телефоне нельзя.
     * Для видео (кадр меняется) кэша нет — только проход по кадру у вызывающего.
     */
    var MAX_AREA = 8e6, LRU_MAX = 4, _lru = [];
    function srcSize(src) {
        var w = src.naturalWidth || src.videoWidth || src.displayWidth || src.codedWidth || src.width || 0;
        var h = src.naturalHeight || src.videoHeight || src.displayHeight || src.codedHeight || src.height || 0;
        return { w: w, h: h };
    }
    function filteredSource(src, planOrCss, needW, needH) {
        var p = typeof planOrCss === 'string' ? plan(planOrCss) : planOrCss;
        if (!src || !p || (!p.groups.length && p.alpha === 1)) return src;
        var sz = srcSize(src);
        if (!sz.w || !sz.h) return src;
        var k = 1;
        if (needW > 0 && needH > 0) k = Math.min(1, Math.max(needW / sz.w, needH / sz.h));
        if (sz.w * sz.h * k * k > MAX_AREA) k = Math.sqrt(MAX_AREA / (sz.w * sz.h));
        var w = Math.max(1, Math.round(sz.w * k)), h = Math.max(1, Math.round(sz.h * k));
        // Видео и кадры декодера меняют содержимое при той же ссылке —
        // кэшировать их значит показывать прошлый кадр.
        var moving = (typeof HTMLVideoElement !== 'undefined' && src instanceof HTMLVideoElement)
            || (typeof VideoFrame !== 'undefined' && src instanceof VideoFrame);
        for (var i = 0; !moving && i < _lru.length; i++) {
            var e = _lru[i];
            if (e.src === src && e.key === p.key && e.w >= w && e.h >= h) {
                if (i) { _lru.splice(i, 1); _lru.unshift(e); }
                return e.canvas;
            }
        }
        var c, g;
        try {
            c = document.createElement('canvas');
            c.width = w; c.height = h;
            g = c.getContext('2d');
            if (!g) return src;
            g.imageSmoothingEnabled = true;
            g.drawImage(src, 0, 0, w, h);
        } catch (_) { return src; }
        if (!applyPixels(g, 0, 0, w, h, p)) return src;
        // Чтобы кэши ниже по течению (размытая подложка) отличали тонированный
        // источник от исходного и от тонированного другим фильтром.
        c.__cssFilterTag = p.key;
        if (moving) return c;
        for (i = _lru.length - 1; i >= 0; i--) if (_lru[i].src === src) _lru.splice(i, 1);
        _lru.unshift({ src: src, key: p.key, w: w, h: h, canvas: c });
        while (_lru.length > LRU_MAX) {
            var old = _lru.pop();
            try { old.canvas.width = old.canvas.height = 0; } catch (_) {}   // WebKit отдаёт память холста только так
        }
        return c;
    }

    /** Тот же фильтр для сплошного цвета (#rgb, #rrggbb, rgb(), rgba()). */
    function color(css, planOrCss) {
        var p = typeof planOrCss === 'string' ? plan(planOrCss) : planOrCss;
        if (!p || (!p.groups.length && p.alpha === 1)) return css;
        var s = String(css || '').trim(), r, g, b, a = 1, m;
        if ((m = /^#([0-9a-f]{3})$/i.exec(s))) {
            r = parseInt(m[1][0] + m[1][0], 16); g = parseInt(m[1][1] + m[1][1], 16); b = parseInt(m[1][2] + m[1][2], 16);
        } else if ((m = /^#([0-9a-f]{6})$/i.exec(s))) {
            r = parseInt(m[1].slice(0, 2), 16); g = parseInt(m[1].slice(2, 4), 16); b = parseInt(m[1].slice(4, 6), 16);
        } else if ((m = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/i.exec(s))) {
            r = +m[1]; g = +m[2]; b = +m[3]; if (m[4] != null) a = +m[4];
        } else {
            return css;   // именованный цвет, градиент — оставить как есть
        }
        // Тот же тип массива, что у пикселей холста: округление и ограничение
        // 0..255 ровно такие же, как в кадре.
        var d = new Uint8ClampedArray([r, g, b, 255]);
        applyToData(d, p);
        var A = a * p.alpha;
        return 'rgba(' + d[0] + ',' + d[1] + ',' + d[2] + ',' + (+A.toFixed(3)) + ')';
    }

    function blurOf(css) {
        var p = plan(css);
        return p ? p.blur : 0;
    }

    /**
     * Нарисовать источник размытым. ctx.filter — где он даёт эффект (сам
     * движок или прокладка выше); иначе «уменьшить-увеличить»: картинка
     * ужимается так, чтобы деталь мельче радиуса исчезла, и растягивается
     * обратно со сглаживанием — последний запасной путь, если прокладку
     * поставить не удалось (холст не читается).
     */
    function drawBlurred(ctx, src, dx, dy, dw, dh, px) {
        px = Math.max(0, +px || 0);
        if (!px) { ctx.drawImage(src, dx, dy, dw, dh); return 'none'; }
        if (worksOn(ctx)) {
            try {
                ctx.filter = 'blur(' + px + 'px)';
                ctx.drawImage(src, dx, dy, dw, dh);
                ctx.filter = 'none';
                return isOffscreen(ctx) ? (PROBE.off === true ? 'native' : 'shim') : (NATIVE ? 'native' : 'shim');
            } catch (_) {}
        }
        var k = Math.max(2, px * 0.9);
        var t = document.createElement('canvas');
        t.width = Math.max(2, Math.round(Math.abs(dw) / k));
        t.height = Math.max(2, Math.round(Math.abs(dh) / k));
        var tx = t.getContext('2d');
        tx.imageSmoothingEnabled = true;
        tx.drawImage(src, 0, 0, t.width, t.height);
        var was = ctx.imageSmoothingEnabled;
        ctx.imageSmoothingEnabled = true;
        ctx.drawImage(t, dx, dy, dw, dh);
        ctx.imageSmoothingEnabled = was;
        return 'scaled';
    }

    var api = {
        // Движок сам применяет ctx.filter на обычном холсте. Кто держит свой
        // быстрый пиксельный путь (слайдшоу), решает по этому флагу.
        native: NATIVE,
        // ctx.filter на обычном холсте ДАЁТ эффект — сам движок или прокладка.
        works: NATIVE || SHIM.dom,
        worksOn: worksOn,
        shimmed: SHIM,
        probe: PROBE,
        // для стендов: сами кирпичи, без прокладки вокруг
        _blurData: blurData,
        _blurCanvas: blurCanvas,
        // размытие, которое прячет (цензура, скрытие области) — см. blurHide
        blurHide: blurHide,
        paddedCopy: paddedCopy,
        sizeOf: srcSize,
        parse: parse,
        plan: plan,
        compose: compose,
        applyToData: applyToData,
        applyPixels: applyPixels,
        begin: begin,
        end: end,
        blurOf: blurOf,
        drawBlurred: drawBlurred,
        filteredSource: filteredSource,
        color: color,
    };
    // Объявление — буквально `window.CanvasCssFilter =`: макет проверяет этот
    // глобал после загрузки (data-expect) и перезапускает лестницу адресов,
    // если файл пришёл обрезанным и не исполнился (сторож
    // CriticalScriptsDeclareExpectedGlobalTest читает именно эту форму).
    if (typeof window !== 'undefined') window.CanvasCssFilter = api;
    else root.CanvasCssFilter = api;
})(typeof window !== 'undefined' ? window : globalThis);
